import React, { useState } from 'react';
import { MapPin, Phone, Mail, Building2, ChevronRight, MapPinned, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Helmet } from 'react-helmet-async';
import { useToast } from '../hooks/use-toast';

const DropOffLocationsPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    province: '',
    city: '',
    postalCode: '',
    serviceType: '',
    weight: '',
    destination: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.phone || !formData.email || !formData.province || !formData.city || !formData.serviceType) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    // Success message
    toast({
      title: "Request Submitted Successfully!",
      description: "Our team will contact you within 24 hours to confirm pickup or drop-off arrangements.",
    });

    // Clear form
    setFormData({
      name: '',
      phone: '',
      email: '',
      province: '',
      city: '',
      postalCode: '',
      serviceType: '',
      weight: '',
      destination: '',
      message: ''
    });
  };

  const openGoogleMaps = () => {
    // Use exact GPS coordinates for Brampton HQ
    window.open('https://www.google.com/maps/dir/?api=1&destination=43.693950%2C-79.693070', '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Shippzy Locations & Drop-Off Services | Brampton, BC & Nova Scotia</title>
        <meta name="description" content="Visit Shippzy's Brampton headquarters or contact our British Columbia and Nova Scotia teams for international shipping, pickup and drop-off assistance." />
        <meta name="keywords" content="Shippzy locations, drop-off locations, Brampton headquarters, pickup service, international shipping Canada" />
        <link rel="canonical" href="https://shippzy.ca/drop-off-locations" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Shippzy Inc.",
            "image": "https://www.shippzy.ca/logo.png",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "140 Advance Blvd, Unit 7",
              "addressLocality": "Brampton",
              "addressRegion": "ON",
              "postalCode": "L6T 4Z8",
              "addressCountry": "CA"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "43.693950",
              "longitude": "-79.693070"
            },
            "telephone": "+14376160758",
            "email": "ship@shippzy.ca",
            "url": "https://www.shippzy.ca",
            "priceRange": "$$",
            "areaServed": ["CA"],
            "serviceType": "International Courier and Shipping Service"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <MapPinned className="inline mr-2 h-4 w-4" />
              Shippzy Locations
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Shippzy Locations & Drop-Off Services
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Visit our Brampton headquarters or connect with our regional teams to arrange your shipment.
            </p>
          </div>

          {/* Brampton Headquarters - PRIMARY LOCATION */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white p-2 rounded-t-xl">
              <p className="text-center font-bold text-sm uppercase tracking-wide">⭐ Official Headquarters</p>
            </div>
            <Card className="border-4 border-red-600 rounded-t-none shadow-2xl">
              <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 pb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-red-600 p-4 rounded-xl">
                    <Building2 className="h-10 w-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                      SHIPPZY INC. — BRAMPTON HEADQUARTERS
                    </CardTitle>
                    <p className="text-gray-700 text-lg">
                      Our Brampton headquarters serves customers looking for international courier, parcel, document and relocation shipping services.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Address & Contact */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-gray-900 mb-1">Full Address:</p>
                        <p className="text-gray-700">140 Advance Blvd, Unit 7</p>
                        <p className="text-gray-700">Brampton, ON L6T 4Z8</p>
                        <p className="text-gray-700">Canada</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Phone className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-bold text-gray-900 mb-1">Phone:</p>
                        <a href="tel:+14376160758" className="text-red-600 hover:text-red-700 font-semibold text-lg">
                          +1 (437) 616-0758
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Mail className="h-6 w-6 text-red-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-gray-900 mb-1">Email:</p>
                        <a href="mailto:ship@shippzy.ca" className="text-red-600 hover:text-red-700 font-semibold">
                          ship@shippzy.ca
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Map Embed */}
                  <div className="bg-gray-100 rounded-lg overflow-hidden h-64">
                    <iframe
                      title="Shippzy Brampton Headquarters Map"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2884.5!2d-79.693070!3d43.693950!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDQxJzM4LjIiTiA3OcKwNDEnMzUuMSJX!5e0!3m2!1sen!2sca!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={openGoogleMaps}
                    className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white px-8 py-6 text-lg"
                  >
                    <MapPin className="mr-2 h-5 w-5" />
                    Get Directions
                  </Button>
                  <Button
                    onClick={() => window.location.href = 'tel:+14376160758'}
                    variant="outline"
                    className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-8 py-6 text-lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Contact Brampton Office
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Regional Support Locations */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Regional Support</h2>
            <div className="grid md:grid-cols-2 gap-8">
              
              {/* British Columbia */}
              <Card className="border-2 border-blue-200 hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-blue-600 p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      SHIPPZY — BRITISH COLUMBIA
                    </CardTitle>
                  </div>
                  <p className="text-gray-700 font-medium">Surrey, British Columbia</p>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <a href="tel:+16726996502" className="text-blue-600 hover:text-blue-700 font-bold text-lg">
                        +1 (672) 699-6502
                      </a>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <a href="mailto:shipwithshippzybc@gmail.com" className="text-blue-600 hover:text-blue-700 font-semibold">
                        shipwithshippzybc@gmail.com
                      </a>
                    </div>
                    
                    <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                      <p className="text-gray-700">
                        For shipping, pickup and drop-off arrangements in British Columbia, please contact our regional team before visiting or sending your shipment.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => window.location.href = 'tel:+16726996502'}
                      className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      Call BC Team
                    </Button>
                    <Button
                      onClick={() => document.getElementById('request-form').scrollIntoView({ behavior: 'smooth' })}
                      variant="outline"
                      className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white w-full"
                    >
                      <Package className="mr-2 h-4 w-4" />
                      Request Pickup / Drop-Off
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Nova Scotia */}
              <Card className="border-2 border-green-200 hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-green-600 p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      SHIPPZY — NOVA SCOTIA
                    </CardTitle>
                  </div>
                  <p className="text-gray-700 font-medium">Nova Scotia, Canada</p>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <a href="tel:+19024490682" className="text-green-600 hover:text-green-700 font-bold text-lg">
                        +1 (902) 449-0682
                      </a>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <a href="mailto:shipwithshippzyns@gmail.com" className="text-green-600 hover:text-green-700 font-semibold">
                        shipwithshippzyns@gmail.com
                      </a>
                    </div>
                    
                    <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
                      <p className="text-gray-700">
                        For international shipping, pickups and drop-off arrangements in Nova Scotia, please contact our regional team.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <Button
                      onClick={() => window.location.href = 'tel:+19024490682'}
                      className="bg-green-600 hover:bg-green-700 text-white w-full"
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      Call Nova Scotia Team
                    </Button>
                    <Button
                      onClick={() => document.getElementById('request-form').scrollIntoView({ behavior: 'smooth' })}
                      variant="outline"
                      className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white w-full"
                    >
                      <Package className="mr-2 h-4 w-4" />
                      Request Pickup / Drop-Off
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Other Canadian Locations */}
          <div className="mb-16">
            <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-2xl">
              <CardContent className="pt-12 pb-12">
                <div className="text-center max-w-3xl mx-auto">
                  <MapPin className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
                  <h2 className="text-3xl font-bold mb-4">
                    Shipping From Another Canadian City?
                  </h2>
                  <p className="text-lg text-gray-300 mb-8">
                    Not located near our Brampton headquarters? Shippzy can review pickup and shipping arrangements for customers in other Canadian cities and provinces.
                  </p>
                  <Button
                    onClick={() => document.getElementById('request-form').scrollIntoView({ behavior: 'smooth' })}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-gray-900 font-bold px-10 py-6 text-lg"
                  >
                    Request Shipping Assistance
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pickup & Drop-Off Request Form */}
          <div id="request-form" className="mb-16 scroll-mt-24">
            <Card className="border-t-4 border-red-600 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
                <CardTitle className="text-3xl font-bold text-gray-900 text-center">
                  Pickup & Drop-Off Request Form
                </CardTitle>
                <p className="text-center text-gray-700 mt-2">
                  Fill out the form below and our team will contact you within 24 hours to arrange your shipment.
                </p>
              </CardHeader>
              <CardContent className="pt-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Full Name */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Full Name <span className="text-red-600">*</span>
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                        className="w-full"
                      />
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Phone Number <span className="text-red-600">*</span>
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+1 (123) 456-7890"
                        required
                        className="w-full"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Email Address <span className="text-red-600">*</span>
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        className="w-full"
                      />
                    </div>

                    {/* Province */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Province <span className="text-red-600">*</span>
                      </label>
                      <select
                        name="province"
                        value={formData.province}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      >
                        <option value="">Select Province</option>
                        <option value="ON">Ontario</option>
                        <option value="BC">British Columbia</option>
                        <option value="NS">Nova Scotia</option>
                        <option value="AB">Alberta</option>
                        <option value="QC">Quebec</option>
                        <option value="MB">Manitoba</option>
                        <option value="SK">Saskatchewan</option>
                        <option value="NB">New Brunswick</option>
                        <option value="PE">Prince Edward Island</option>
                        <option value="NL">Newfoundland and Labrador</option>
                        <option value="YT">Yukon</option>
                        <option value="NT">Northwest Territories</option>
                        <option value="NU">Nunavut</option>
                      </select>
                    </div>

                    {/* City */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        City <span className="text-red-600">*</span>
                      </label>
                      <Input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Toronto"
                        required
                        className="w-full"
                      />
                    </div>

                    {/* Postal Code */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Postal Code
                      </label>
                      <Input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="M1A 1A1"
                        className="w-full"
                      />
                    </div>

                    {/* Service Type */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Service Required <span className="text-red-600">*</span>
                      </label>
                      <select
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      >
                        <option value="">Select Service</option>
                        <option value="pickup">Pickup</option>
                        <option value="dropoff">Drop-off</option>
                        <option value="inquiry">International Shipping Inquiry</option>
                      </select>
                    </div>

                    {/* Approximate Weight */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Approximate Shipment Weight
                      </label>
                      <Input
                        type="text"
                        name="weight"
                        value={formData.weight}
                        onChange={handleInputChange}
                        placeholder="e.g., 10 kg"
                        className="w-full"
                      />
                    </div>

                    {/* Destination Country */}
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        Destination Country
                      </label>
                      <Input
                        type="text"
                        name="destination"
                        value={formData.destination}
                        onChange={handleInputChange}
                        placeholder="e.g., India"
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Additional Message */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      Additional Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      placeholder="Please provide any additional details about your shipment..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="text-center">
                    <Button
                      type="submit"
                      className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white px-12 py-6 text-lg font-bold"
                    >
                      Submit Request
                    </Button>
                    <p className="text-sm text-gray-600 mt-4">
                      <span className="text-red-600">*</span> Required fields
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Back to Home */}
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-3"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DropOffLocationsPage;
